import type React from 'react';
import { useUrlLocalizer, useTranslator } from 'tradera/lang/translate';
import CloseNarrowIcon from '@tradera/blueprint/theme/icons/close-narrow.svg?jsx';
import SearchIcon from '@tradera/blueprint/theme/icons2/search.svg?jsx';
import { Button } from 'tradera/components/atoms/button/button';
import styles from './search-input.module.scss';
import { classNames } from 'tradera/utils/class-names';

type SearchInputProps = {
    categoriesElement?: React.ReactNode;
    formClassName?: string;
    inputClassName?: string;
    onBlur?: () => void;
    onClearSearchInput: (event: React.MouseEvent<HTMLElement>) => void;
    onFocus?: () => void;
    onFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    onUserInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onUserKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    searchInputRef: React.RefObject<HTMLInputElement>;
    searchTerm: string;
    selectedCategory?: {
        id: number;
        name: string;
    };
    suggestionsElement?: React.ReactNode;
    placeholderText?: string;
    focusOverride?: () => void;
    /**
     * Defaults to true
     */
    border?: boolean;
};

export const SearchInput = ({
    categoriesElement = null,
    formClassName,
    inputClassName = '',
    onBlur = () => {},
    onClearSearchInput = () => {},
    onFocus = () => {},
    onFormSubmit = () => {},
    onUserInputChange = () => {},
    onUserKeyUp = () => {},
    searchInputRef,
    searchTerm,
    selectedCategory,
    suggestionsElement = null,
    placeholderText,
    focusOverride,
    border = true
}: SearchInputProps) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const { t } = useTranslator();

    const placeHolderTranslation =
        selectedCategory && selectedCategory.id !== 0 && selectedCategory.name
            ? t('siteHeader_categorySearchPlaceholder', {
                  categoryName: selectedCategory.name
              })
            : t('siteHeader_generalSearchPlaceholder');

    const showClearButton = searchTerm !== '';

    return (
        <form
            action={toLocalizedUrl('/search')}
            method="GET"
            autoComplete="off"
            onSubmit={onFormSubmit}
            className={classNames(
                'row no-gutters',
                styles.searchInputForm,
                border && styles.searchInputFormBorder,
                formClassName
            )}
            data-nav-search
            role="search"
            onClick={(e) => {
                if (focusOverride) {
                    e.preventDefault();
                    e.stopPropagation();
                    focusOverride();
                }
            }}>
            <div
                className={classNames('col h-100', {
                    'pointer-events-none': focusOverride
                })}>
                <input
                    aria-label={placeholderText || placeHolderTranslation}
                    type="text"
                    id={`site-header-search-input-${inputClassName}`}
                    className={classNames(
                        'form-control focus-visible',
                        styles.searchInputField,
                        styles.formControl,
                        inputClassName
                    )}
                    data-search-field
                    data-testid="search-input"
                    name="q"
                    placeholder={placeholderText || placeHolderTranslation}
                    maxLength={98}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    ref={searchInputRef}
                    value={searchTerm || ''}
                    onChange={onUserInputChange}
                    onKeyDown={onUserKeyUp}
                />
                <div className="position-relative">{suggestionsElement}</div>
                <fieldset className={styles.searchInputFormButtons}>
                    {showClearButton ? (
                        <Button
                            theme="unbutton"
                            aria-label="Reset input"
                            type="reset"
                            data-search-clear
                            className={classNames(
                                'h-100 px-2 focus-visible-inner text-gray-600'
                            )}
                            onClick={(event) => {
                                onClearSearchInput?.(event);
                                // We need to delay the focus, probably because the clearing is through a
                                // React set state which is performed in the next tick. Without the setTimeout,
                                // the input will be focused but the content will not be cleared.
                                setTimeout(() => {
                                    searchInputRef.current?.focus();
                                });
                            }}>
                            <CloseNarrowIcon className="icon icon-xs" />
                        </Button>
                    ) : (
                        <Button
                            theme="unbutton"
                            aria-label="Search"
                            fluid
                            type="submit"
                            data-search-submit
                            className={classNames(
                                'h-100 px-2 focus-visible-inner text-gray-600'
                            )}>
                            <SearchIcon className="icon" />
                        </Button>
                    )}
                </fieldset>
            </div>
            <div className="d-none d-md-flex col-md-auto">
                {categoriesElement}
                <input
                    type="hidden"
                    name="categoryId"
                    value={selectedCategory?.id ?? 0}
                />
            </div>
        </form>
    );
};
